import React, { useEffect, useState } from 'react';
import { useFormik, FieldArray, FormikProvider, FormikErrors } from 'formik';
import * as yup from 'yup';
import InputField from 'components/shared/input-field';
import Button from 'components/shared/button';
import CountrySelect from 'components/shared/select-input/countryInput';
import SelectInput from 'components/shared/select-input';
import useFetchStates from 'hooks/useFetchStates';
import { formatWithCommas } from 'functions/currencyConveter';
import { useDropzone } from 'react-dropzone';
import { FiX } from 'react-icons/fi';

interface ChildInfo {
  ChildFirstName: string;
  ChildLastName: string;
  SchoolFees: string;
  SchoolName: string;
  SchoolLocation: string;
  SchoolAddress: string;
  Address2: string;
  City: string;
  PostalCode: string;
  Country: string;
  ChildGrade: string;
  invoice: File | null;
}

interface Step2Props {
  inputData: ChildInfo[];
  setInputData: (data: { ChildDetails: ChildInfo[] }) => void;
  errors: { [key: string]: string };
  clearErrors: (field: string) => void;
  nextStep: () => void;
}

const MyChildLoanApplStep1: React.FC<Step2Props> = ({
  inputData,
  setInputData,
  clearErrors,
  nextStep,
}) => {
  const [fileError, setFileError] = useState<string | null>(null);
  const { data: stateOptions } = useFetchStates();

  const formik = useFormik<{
    LoanAmount: string;
    ChildDetails: ChildInfo[];
  }>({
    initialValues: {
      LoanAmount: '',
      ChildDetails:
        inputData.length > 0
          ? inputData
          : [
              {
                ChildFirstName: '',
                ChildLastName: '',
                SchoolFees: '',
                SchoolLocation: '',
                SchoolAddress: '',
                Address2: '',
                City: '',
                PostalCode: '',
                Country: '',
                SchoolName: '',
                ChildGrade: '',
                invoice: null,
              },
            ],
    },
    validationSchema: yup.object({
      LoanAmount: yup.string().required('Loan amount is required'),
      ChildDetails: yup.array().of(
        yup.object({
          ChildFirstName: yup
            .string()
            .required("Child's first name is required"),
          ChildLastName: yup.string().required("Child's last name is required"),
          SchoolFees: yup.string().required('School fee is required'),
          SchoolName: yup.string().required('School name is required'),
          ChildGrade: yup.string().required("Child's grade is required"),
          SchoolLocation: yup.string().required('State is required'),
          SchoolAddress: yup.string().required('School address is required'),
          City: yup.string().required('City is required'),

          invoice: yup
            .mixed()
            .required('Invoice is required')
            .test(
              'fileSize',
              'File size is too large. Maximum size is 5MB.',
              (file: any) => {
                return file ? file.size <= 5 * 1024 * 1024 : true;
              }
            ),
        })
      ),
    }),
    onSubmit: (values) => {
      setInputData({ ChildDetails: values?.ChildDetails });
      clearErrors('');
    },
  });

  useEffect(() => {
    setInputData(formik.values);
  }, [formik.values]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index?: number
  ) => {
    const { name, value } = e.target;

    const plainValue = value.replace(/,/g, '');
    if (name === `ChildDetails.${index}.SchoolFees` && index !== undefined) {
      const plainValue = value.replace(/,/g, '');
      const formattedValue = plainValue ? formatWithCommas(plainValue) : '';
      formik.setFieldValue(`ChildDetails.${index}.SchoolFees`, formattedValue);
    } else if (
      name === `ChildDetails.${index}.LoanAmount` &&
      index !== undefined
    ) {
      const formattedValue = formatWithCommas(plainValue);
      formik.setFieldValue(`ChildDetails.${index}.LoanAmount`, formattedValue);
      formik.handleChange(e);
    } else if (name === `LoanAmount`) {
      const formattedValue = formatWithCommas(plainValue);
      formik.setFieldValue(`LoanAmount`, formattedValue);
      formik.handleChange(e);
    } else {
      formik.handleChange(e);
    }

    clearErrors(name);
  };

  const onDropInvoice = (acceptedFiles: File[], index: number) => {
    const currentFile = formik.values.ChildDetails[index].invoice || null;
    // setFileError('Invoice is required');
    const filteredFile = acceptedFiles.find(
      (file) => file.size <= 5 * 1024 * 1024
    );
    if (!filteredFile) {
      setFileError('File is too large. Maximum file size is 5MB.');
    } else if (filteredFile && currentFile !== null) {
      setFileError('Only one invoice can be uploaded per child.');
      return;
    } else {
      setFileError(null);
      formik.setFieldValue(`ChildDetails[${index}].invoice`, filteredFile);
    }
  };

  const removeDocument = (index: number) => {
    formik.setFieldValue(`ChildDetails[${index}].invoice`, null);
  };

  const isFormComplete = formik.values.ChildDetails.every(
    (child) =>
      child.ChildFirstName &&
      child.ChildLastName &&
      child.SchoolFees &&
      // child.Country &&
      child.SchoolName &&
      child.ChildGrade &&
      child.SchoolLocation &&
      (child.SchoolAddress || child.Address2) &&
      child.City &&
      child.invoice
  );

  return (
    <div className="w-full  scrollbar-always-visible">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl fomt-semibold">Child and School Details</h1>
        <p className="text-lg text-gray-2">Complete the fields below</p>
      </div>
      <hr className="my-4" />
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <InputField
            label="Loan Amount"
            type="text"
            name="LoanAmount"
            placeholder="Loan Amount"
            value={formatWithCommas(formik.values.LoanAmount.toString())}
            onChange={handleInputChange}
            onBlur={formik.handleBlur}
            error={formik.errors.LoanAmount}
            required
          />
          <FieldArray name="ChildDetails">
            {({ push, remove }) => (
              <>
                {formik.values.ChildDetails.map((child, index) => (
                  <div
                    key={`${index}-${child}`}
                    className="w-full p-4 rounded md:grid sm:block sm:grid-cols-1 md:grid-cols-2 gap-4 mt-4"
                  >
                    <div className="col-span-1">
                      <InputField
                        label="Child's First Name"
                        type="text"
                        name={`ChildDetails.${index}.ChildFirstName`}
                        placeholder="Child's First Name"
                        value={formik.values.ChildDetails[index].ChildFirstName}
                        onChange={handleInputChange}
                        onBlur={formik.handleBlur}
                        required
                        error={
                          (
                            formik.errors.ChildDetails?.[
                              index
                            ] as FormikErrors<ChildInfo>
                          )?.ChildFirstName
                            ? (
                                formik.errors.ChildDetails?.[
                                  index
                                ] as FormikErrors<ChildInfo>
                              )?.ChildFirstName
                            : undefined
                        }
                      />
                    </div>
                    <div className="col-span-1">
                      <InputField
                        type="text"
                        label="Child's Last Name"
                        name={`ChildDetails.${index}.ChildLastName`}
                        placeholder="Child's Last Name"
                        value={formik.values.ChildDetails[index].ChildLastName}
                        onChange={handleInputChange}
                        onBlur={formik.handleBlur}
                        required
                        error={
                          (
                            formik.errors.ChildDetails?.[
                              index
                            ] as FormikErrors<ChildInfo>
                          )?.ChildLastName
                            ? (
                                formik.errors.ChildDetails?.[
                                  index
                                ] as FormikErrors<ChildInfo>
                              )?.ChildLastName
                            : undefined
                        }
                      />
                    </div>
                    <div className="col-span-1">
                      <InputField
                        type="text"
                        label="Child's Grade (Class)"
                        name={`ChildDetails.${index}.ChildGrade`}
                        placeholder="e.g JSS 1"
                        value={formik.values.ChildDetails[index].ChildGrade}
                        onChange={handleInputChange}
                        onBlur={formik.handleBlur}
                        required
                        error={
                          (
                            formik.errors.ChildDetails?.[
                              index
                            ] as FormikErrors<ChildInfo>
                          )?.ChildGrade
                            ? (
                                formik.errors.ChildDetails?.[
                                  index
                                ] as FormikErrors<ChildInfo>
                              )?.ChildGrade
                            : undefined
                        }
                      />
                    </div>
                    <div className="col-span-1">
                      <InputField
                        label="School Name"
                        type="text"
                        name={`ChildDetails.${index}.SchoolName`}
                        placeholder="School Name"
                        value={formik.values.ChildDetails[index].SchoolName}
                        onChange={handleInputChange}
                        onBlur={formik.handleBlur}
                        required
                        error={
                          (
                            formik.errors.ChildDetails?.[
                              index
                            ] as FormikErrors<ChildInfo>
                          )?.SchoolName
                            ? (
                                formik.errors.ChildDetails?.[
                                  index
                                ] as FormikErrors<ChildInfo>
                              )?.SchoolName
                            : undefined
                        }
                      />
                    </div>
                    <CountrySelect
                      name={`ChildDetails.${index}.Country`}
                      value={formik?.values?.ChildDetails[index]?.Country}
                      setFieldValue={formik.setFieldValue}
                      onChange={handleInputChange}
                      placeholder="Choose Country"
                      required={true}
                    />

                    <div className="col-span-1">
                      <InputField
                        type="text"
                        label="School Address 1"
                        name={`ChildDetails.${index}.SchoolAddress`}
                        placeholder="e.g No 1 Crescent Ave"
                        value={formik.values.ChildDetails[index].SchoolAddress}
                        onChange={handleInputChange}
                        onBlur={formik.handleBlur}
                        required
                        error={
                          (
                            formik.errors.ChildDetails?.[
                              index
                            ] as FormikErrors<ChildInfo>
                          )?.SchoolAddress
                            ? (
                                formik.errors.ChildDetails?.[
                                  index
                                ] as FormikErrors<ChildInfo>
                              )?.SchoolAddress
                            : undefined
                        }
                      />
                    </div>
                    <div className="col-span-1">
                      <InputField
                        type="text"
                        label="Address 2(Optional)"
                        name={`ChildDetails.${index}.Address2`}
                        placeholder="e.g No 13 Crescent Ave"
                        value={formik.values.ChildDetails[index].Address2}
                        onChange={handleInputChange}
                        onBlur={formik.handleBlur}
                      />
                    </div>
                    <div className="col-span-1">
                      <InputField
                        type="text"
                        label="City"
                        name={`ChildDetails.${index}.City`}
                        placeholder="e.g Lagos"
                        value={formik.values.ChildDetails[index].City}
                        onChange={handleInputChange}
                        onBlur={formik.handleBlur}
                        required
                        error={
                          (
                            formik.errors.ChildDetails?.[
                              index
                            ] as FormikErrors<ChildInfo>
                          )?.City
                            ? (
                                formik.errors.ChildDetails?.[
                                  index
                                ] as FormikErrors<ChildInfo>
                              )?.City
                            : undefined
                        }
                      />
                    </div>
                    <div className="col-span-1 state-input">
                      <SelectInput
                        label="State/Province/Region"
                        options={stateOptions}
                        required={true}
                        name={`ChildDetails.${index}.SchoolLocation`}
                        value={
                          stateOptions.find(
                            (option) =>
                              option?.label ===
                              formik?.values?.ChildDetails[index]
                                ?.SchoolLocation
                          ) || null
                        }
                        onChange={(name, value) => {
                          formik.setFieldValue(name, value?.label);
                        }}
                        error={
                          (
                            formik.errors.ChildDetails?.[
                              index
                            ] as FormikErrors<ChildInfo>
                          )?.SchoolLocation
                            ? (
                                formik.errors.ChildDetails?.[
                                  index
                                ] as FormikErrors<ChildInfo>
                              )?.SchoolLocation
                            : undefined
                        }
                      />
                    </div>

                    <div className="col-span-1">
                      <InputField
                        type="text"
                        label="Zip/Postal Code"
                        name={`ChildDetails.${index}.PostalCode`}
                        placeholder="e.g 101245"
                        value={formik.values.ChildDetails[index].PostalCode}
                        onChange={handleInputChange}
                        onBlur={formik.handleBlur}
                        // required
                      />
                    </div>

                    <div className="col-span-1">
                      <InputField
                        type="text"
                        label="School Fee"
                        name={`ChildDetails.${index}.SchoolFees`}
                        placeholder="School Fees"
                        value={formatWithCommas(
                          formik.values.ChildDetails[
                            index
                          ].SchoolFees.toString()
                        )}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleInputChange(e, index)
                        }
                        onBlur={formik.handleBlur}
                        required
                        error={
                          (
                            formik.errors.ChildDetails?.[
                              index
                            ] as FormikErrors<ChildInfo>
                          )?.SchoolFees
                            ? (
                                formik.errors.ChildDetails?.[
                                  index
                                ] as FormikErrors<ChildInfo>
                              )?.SchoolFees
                            : undefined
                        }
                      />
                    </div>

                    {/* Upload Section */}
                    <div className="col-span-1">
                      <p>Child's School Fee Invoice</p>
                      <div className="flex items-center justify-center flex-col border rounded-md p-3">
                        <DropzoneComponent
                          index={index}
                          onDropInvoice={onDropInvoice}
                          formik={formik}
                        />
                        {fileError && (
                          <p className="text-red-500 text-xs text-center">
                            {fileError}
                          </p>
                        )}

                        {/* Display the single uploaded file */}
                        {formik.values.ChildDetails[index].invoice && (
                          <ul className="mt-2">
                            <li className="flex justify-between items-center">
                              <span>
                                {
                                  formik.values.ChildDetails[index]?.invoice
                                    ?.name
                                }
                              </span>
                              <button
                                type="button"
                                onClick={() => removeDocument(index)}
                                className="text-red-500"
                              >
                                <FiX />
                              </button>
                            </li>
                          </ul>
                        )}
                      </div>
                    </div>

                    <div className=" col-span-2 flex items-center justify-between">
                      {formik.values.ChildDetails.length > 1 && (
                        <button
                          type="button"
                          className="flex text-red-500 items-center gap-1"
                          onClick={() => remove(index)}
                        >
                          Remove
                        </button>
                      )}
                      <button
                        type="button"
                        className="text-BrandPrimary flex items-center gap-1"
                        onClick={() => {
                          push({
                            ChildFirstName: '',
                            ChildLastName: '',
                            SchoolFees: '',
                            SchoolLocation: '',
                            SchoolAddress: '',
                            Address2: '',
                            City: '',
                            PostalCode: '',
                            Country: '',
                            SchoolName: '',
                            ChildGrade: '',
                            invoice: null,
                          });
                          setFileError(null);
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          className="lucide lucide-square-plus"
                        >
                          <rect width="18" height="18" x="3" y="3" rx="2" />
                          <path d="M8 12h8" />
                          <path d="M12 8v8" />
                        </svg>
                        Add Another Child
                      </button>
                    </div>
                  </div>
                ))}
                <div className="flex justify-end gap-3 float-end">
                  <Button
                    type="button"
                    disabled={!isFormComplete}
                    onClick={() => nextStep()}
                    className="w-full"
                  >
                    Next
                  </Button>
                </div>
              </>
            )}
          </FieldArray>
        </form>
      </FormikProvider>
    </div>
  );
};

export default MyChildLoanApplStep1;

const DropzoneComponent: React.FC<{
  index: number;
  formik: any;
  onDropInvoice: (files: File[], index: number) => void;
}> = ({ index, formik, onDropInvoice }) => {
  const { getRootProps, getInputProps } = useDropzone({
    accept: { 'image/jpeg': [], 'application/pdf': [] },
    maxSize: 5 * 1024 * 1024,
    multiple: false,
    onDrop: (acceptedFiles) => onDropInvoice(acceptedFiles, index),
  });

  return (
    <div
      {...getRootProps()}
      className="flex items-center justify-center flex-col"
    >
      <input {...getInputProps()} />
      <img src="/Assets/upload.svg" className="w-12" alt="upload" />
      <p>
        <span className="text-blue-500 font-medium">Click to upload</span> or
        drag and drop
      </p>
      <p className="text-xs">.jpeg or .pdf (max. 5MB)</p>
      {/* {formik?.errors.ChildDetails?.[index]?.invoice
        ? formik?.errors.ChildDetails?.[index]?.invoice
        : undefined} */}
      {/* {(formik?.errors.ChildDetails?.[index] as FormikErrors<ChildInfo>)
        ?.invoice
        ? (formik?.errors.ChildDetails?.[index] as FormikErrors<ChildInfo>)
            ?.invoice
        : undefined} */}
      {formik.errors.ChildDetails?.[index]?.invoice ? (
        <div className="text-red-500 text-xs text-center">
          {formik.errors.ChildDetails?.[index]?.invoice}
        </div>
      ) : null}
    </div>
  );
};
